import React, { useEffect, useMemo, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { SupplierLayout } from '../../components/SupplierLayout';
import { useSupplierAuth } from '../../lib/supplierAuthStore';
import { useSupplierStore } from '../../lib/supplierStore';
import { toast } from 'sonner';
import { Building2, Mail, Phone, MapPin, FileText, CheckCircle2, Sparkles, Truck, Plus, X, Send, Wand2, Route, Tag, MapPinned, MoreVertical, Trash2, Edit2, AlertCircle, ChevronDown, ArrowRight, Shield, Users, Lock, UserPlus, Eye, EyeOff, Settings2, Zap, Search, Check } from 'lucide-react';
type ChatMsg = {
  id: string;
  from: 'ai' | 'user';
  text: string;
  suggestions?: Suggestion[];
};
const UAE_CITIES = ['Dubai', 'Abu Dhabi', 'Sharjah', 'Ajman', 'Ras Al Khaimah', 'Fujairah', 'Umm Al Quwain', 'Al Ain'];
// Mapping from truck type keywords to suggested services.
type Suggestion = {
  service: string;
  rationale: string;
  basedOn: string[];
};
function suggestServicesFromTrucks(truckTypes: string[]): Suggestion[] {
  const set = (s: string) => s.toLowerCase();
  const types = truckTypes.map(set);
  const suggestions: Suggestion[] = [];
  const has = (kw: string) => types.some(t => t.includes(kw));
  const matching = (kw: string) => truckTypes.filter(t => t.toLowerCase().includes(kw));
  if (has('recovery')) {
    suggestions.push({
      service: 'Flat Tire Repair',
      rationale: 'Your recovery crew can carry a portable inflator and patch kit — high-margin roadside add-on.',
      basedOn: matching('recovery')
    });
    suggestions.push({
      service: 'Jump Start',
      rationale: 'Quick add-on for recovery trucks — bundles naturally with Battery Boost.',
      basedOn: matching('recovery')
    });
    suggestions.push({
      service: 'Fuel Delivery',
      rationale: 'Many breakdowns are just empty tanks. A small fuel canister on recovery runs is pure margin.',
      basedOn: matching('recovery')
    });
    suggestions.push({
      service: 'Lockout Assistance',
      rationale: 'Pair with a certified locksmith partner to capture roadside lockout calls.',
      basedOn: matching('recovery')
    });
    suggestions.push({
      service: 'Car Movement',
      rationale: 'Recovery flatbeds double up nicely for showroom and dealer transport between cities.',
      basedOn: matching('recovery')
    });
  }
  if (has('box') || has('curtain')) {
    suggestions.push({
      service: 'Office Move',
      rationale: 'Box and curtain-side trucks handle desks, monitors and IT crates comfortably.',
      basedOn: [...matching('box'), ...matching('curtain')]
    });
    suggestions.push({
      service: 'Storage Pickup & Delivery',
      rationale: 'Reposition customer belongings from self-storage units — recurring monthly demand.',
      basedOn: [...matching('box'), ...matching('curtain')]
    });
  }
  if (has('pickup')) {
    suggestions.push({
      service: 'Last-mile Delivery',
      rationale: 'Pickups are agile for B2B parcel and small load delivery.',
      basedOn: matching('pickup')
    });
    suggestions.push({
      service: 'Appliance Delivery & Install',
      rationale: 'Fridges, washers, ACs — bundle delivery with install for higher ticket size.',
      basedOn: matching('pickup')
    });
  }
  if (has('flatbed') && !has('recovery')) {
    suggestions.push({
      service: 'B2B Pallet Delivery',
      rationale: 'Flatbeds handle palletised cargo and long loads efficiently.',
      basedOn: matching('flatbed').filter(t => !t.toLowerCase().includes('recovery'))
    });
  }
  if (has('low-bed') || has('40-ton') || has('flatbed 12')) {
    suggestions.push({
      service: 'Heavy Equipment Transport',
      rationale: 'Your low-bed and 12m flatbed can carry excavators, generators and oversized loads.',
      basedOn: [...matching('low-bed'), ...matching('flatbed 12')]
    });
    suggestions.push({
      service: 'Construction Site Logistics',
      rationale: 'Recurring contracts moving equipment and materials between active sites.',
      basedOn: [...matching('low-bed'), ...matching('flatbed 12')]
    });
  }
  if (has('refrigerat') || has('reefer') || has('chill')) {
    suggestions.push({
      service: 'Refrigerated Transport',
      rationale: 'Cold-chain capability — perishables, pharmacy, and grocery runs.',
      basedOn: [...matching('refrigerat'), ...matching('reefer')]
    });
  }
  return suggestions;
}
export function SupplierProfile() {
  const {
    user
  } = useSupplierAuth();
  const {
    trucks,
    services,
    addService,
    removeService,
    pricingGroups,
    serviceGroupMap,
    addPricingGroup,
    updatePricingGroup,
    deletePricingGroup,
    assignServiceGroup,
    autoDispatch,
    updateAutoDispatch
  } = useSupplierStore();
  const truckTypes = useMemo(() => Array.from(new Set(trucks.map(t => t.type))), [trucks]);
  const allSuggestions = useMemo(() => suggestServicesFromTrucks(truckTypes), [truckTypes]);
  // Suggestions not yet added to services
  const pendingSuggestions = useMemo(() => allSuggestions.filter(s => !services.some(sv => sv.toLowerCase() === s.service.toLowerCase())), [allSuggestions, services]);
  const [activeTab, setActiveTab] = useState<'business' | 'services' | 'pricing' | 'autodispatch' | 'users' | 'security'>('business');
  const TABS = [{
    id: 'business',
    label: 'Business Details',
    icon: Building2
  }, {
    id: 'services',
    label: 'AI Service Advisor',
    icon: Sparkles
  }, {
    id: 'pricing',
    label: 'Service Pricing',
    icon: Tag
  }, {
    id: 'autodispatch',
    label: 'Auto Dispatch',
    icon: Zap
  }, {
    id: 'users',
    label: 'Users & Roles',
    icon: Users
  }, {
    id: 'security',
    label: 'Security',
    icon: Shield
  }] as const;
  return <SupplierLayout data-id="element-4090">
      <div className="max-w-6xl mx-auto" data-id="element-4091">
        <h1 className="text-2xl font-display font-bold text-gray-900 mb-6" data-id="element-4092">
          Profile & Settings
        </h1>

        <div className="flex flex-col lg:flex-row gap-8" data-id="element-4093">
          {/* Sidebar Navigation */}
          <div className="w-full lg:w-64 shrink-0" data-id="element-4094">
            {/* Mobile/Tablet Horizontal Scroll */}
            <div className="flex lg:hidden overflow-x-auto pb-4 gap-2 scrollbar-hide" data-id="element-4095">
              {TABS.map(tab => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.id;
              return <button key={tab.id} onClick={() => setActiveTab(tab.id)} className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-bold whitespace-nowrap transition-colors ${isActive ? 'bg-gray-900 text-white' : 'bg-white text-gray-600 hover:bg-gray-50 border border-gray-200'}`} data-id="element-4096">
                    <Icon className={`w-4 h-4 ${isActive ? 'text-white' : 'text-gray-400'}`} data-id="element-4097" />
                    {tab.label}
                  </button>;
            })}
            </div>

            {/* Desktop Vertical Sidebar */}
            <div className="hidden lg:flex flex-col gap-1 sticky top-24" data-id="element-4098">
              {TABS.map(tab => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.id;
              return <button key={tab.id} onClick={() => setActiveTab(tab.id)} className={`flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-bold transition-all ${isActive ? 'bg-gray-900 text-white shadow-md' : 'text-gray-600 hover:bg-white hover:text-gray-900'}`} data-id="element-4099">
                    <Icon className={`w-5 h-5 ${isActive ? 'text-white' : 'text-gray-400'}`} data-id="element-4100" />
                    {tab.label}
                  </button>;
            })}
            </div>
          </div>

          {/* Main Content Area */}
          <div className="flex-1 min-w-0" data-id="element-4101">
            <AnimatePresence mode="wait" data-id="element-4102">
              <motion.div key={activeTab} initial={{
              opacity: 0,
              y: 10
            }} animate={{
              opacity: 1,
              y: 0
            }} exit={{
              opacity: 0,
              y: -10
            }} transition={{
              duration: 0.2
            }} data-id="element-4103">
                {activeTab === 'business' && <BusinessTab data-id="element-4104" />}
                {activeTab === 'services' && <ServicesTab data-id="element-4105" />}
                {activeTab === 'pricing' && <PricingTab data-id="element-4106" />}
                {activeTab === 'autodispatch' && <AutoDispatchTab data-id="element-4107" />}
                {activeTab === 'users' && <UsersTab data-id="element-4108" />}
                {activeTab === 'security' && <SecurityTab data-id="element-4109" />}
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </div>
    </SupplierLayout>;
  function AutoDispatchTab() {
    const [selectedTruckType, setSelectedTruckType] = useState<string | null>(truckTypes[0] || null);
    const [serviceSearch, setServiceSearch] = useState('');
    const handleToggleService = (truckType: string, service: string) => {
      const currentMap = autoDispatch.truckServiceMap || {};
      const currentServices = currentMap[truckType] || [];
      const isSelected = currentServices.includes(service);
      const newServices = isSelected ? currentServices.filter(s => s !== service) : [...currentServices, service];
      updateAutoDispatch({
        truckServiceMap: {
          ...currentMap,
          [truckType]: newServices
        }
      });
    };
    const handleBulkSet = (truckType: string, all: boolean) => {
      const currentMap = autoDispatch.truckServiceMap || {};
      updateAutoDispatch({
        truckServiceMap: {
          ...currentMap,
          [truckType]: all ? [...services] : []
        }
      });
    };
    const filteredServices = services.filter(s => s.toLowerCase().includes(serviceSearch.toLowerCase()));
    const selectedEnabled = selectedTruckType ? autoDispatch.truckServiceMap?.[selectedTruckType] || [] : [];
    return <div className="space-y-6" data-id="element-4110">
        <div className="bg-white rounded-2xl border border-gray-200 p-6" data-id="element-4111">
          <div className="flex items-start justify-between mb-6" data-id="element-4112">
            <div data-id="element-4113">
              <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2" data-id="element-4114">
                <Zap className="w-5 h-5 text-blue-600" data-id="element-4115" />
                Auto Dispatch Engine
              </h2>
              <p className="text-sm text-gray-500 mt-1" data-id="element-4116">
                Automatically assign incoming jobs to the best available driver
                based on your custom rules.
              </p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer" data-id="element-4117">
              <input type="checkbox" className="sr-only peer" checked={autoDispatch.enabled} onChange={e => updateAutoDispatch({
              enabled: e.target.checked
            })} data-id="element-4118" />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600" data-id="element-4119"></div>
            </label>
          </div>

          <div className={`space-y-8 transition-opacity duration-200 ${!autoDispatch.enabled ? 'opacity-50 pointer-events-none' : ''}`} data-id="element-4120">
            {/* Algorithm Weights */}
            <div data-id="element-4121">
              <h3 className="text-sm font-bold text-gray-900 mb-4 flex items-center gap-2" data-id="element-4122">
                <Settings2 className="w-4 h-4 text-gray-500" data-id="element-4123" />
                Algorithm Weights
              </h3>
              <div className="space-y-6" data-id="element-4124">
                <div data-id="element-4125">
                  <div className="flex justify-between text-sm mb-2" data-id="element-4126">
                    <span className="font-medium text-gray-700" data-id="element-4127">
                      Distance (Nearest Driver)
                    </span>
                    <span className="text-gray-500" data-id="element-4128">
                      {autoDispatch.weightDistance}%
                    </span>
                  </div>
                  <input type="range" min="0" max="100" value={autoDispatch.weightDistance} onChange={e => updateAutoDispatch({
                  weightDistance: parseInt(e.target.value)
                })} className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-blue-600" data-id="element-4129" />
                  <p className="text-xs text-gray-500 mt-1" data-id="element-4130">
                    Prioritize drivers closest to the pickup location.
                  </p>
                </div>

                <div data-id="element-4131">
                  <div className="flex justify-between text-sm mb-2" data-id="element-4132">
                    <span className="font-medium text-gray-700" data-id="element-4133">
                      Workload (Fewer Active Jobs)
                    </span>
                    <span className="text-gray-500" data-id="element-4134">
                      {autoDispatch.weightWorkload}%
                    </span>
                  </div>
                  <input type="range" min="0" max="100" value={autoDispatch.weightWorkload} onChange={e => updateAutoDispatch({
                  weightWorkload: parseInt(e.target.value)
                })} className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-blue-600" data-id="element-4135" />
                  <p className="text-xs text-gray-500 mt-1" data-id="element-4136">
                    Prioritize drivers with lighter schedules to balance
                    workload.
                  </p>
                </div>

                <div data-id="element-4137">
                  <div className="flex justify-between text-sm mb-2" data-id="element-4138">
                    <span className="font-medium text-gray-700" data-id="element-4139">
                      Driver Rating
                    </span>
                    <span className="text-gray-500" data-id="element-4140">
                      {autoDispatch.weightRating}%
                    </span>
                  </div>
                  <input type="range" min="0" max="100" value={autoDispatch.weightRating} onChange={e => updateAutoDispatch({
                  weightRating: parseInt(e.target.value)
                })} className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-blue-600" data-id="element-4141" />
                  <p className="text-xs text-gray-500 mt-1" data-id="element-4142">
                    Prioritize drivers with higher customer ratings.
                  </p>
                </div>
              </div>
            </div>

            <hr className="border-gray-100" data-id="element-4143" />

            {/* Service Capabilities */}
            <div data-id="element-4144">
              <h3 className="text-sm font-bold text-gray-900 mb-1 flex items-center gap-2" data-id="element-4145">
                <Truck className="w-4 h-4 text-gray-500" data-id="element-4146" />
                Truck Capabilities
              </h3>
              <p className="text-sm text-gray-500 mb-4" data-id="element-4147">
                Pick a truck type, then choose which services it can perform.
                The engine only auto-dispatches jobs to capable trucks.
              </p>

              {truckTypes.length === 0 ? <div className="text-sm text-gray-500 text-center py-8 border-2 border-dashed border-gray-200 rounded-xl" data-id="element-4148">
                  No trucks available. Add trucks in the Trucks module first.
                </div> : <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-4 border border-gray-200 rounded-xl overflow-hidden" data-id="element-4149">
                  {/* Left: Truck type list */}
                  <div className="lg:border-r border-gray-200 lg:max-h-[460px] lg:overflow-y-auto bg-gray-50/50" data-id="element-4150">
                    <div className="lg:hidden p-3 border-b border-gray-200 bg-white" data-id="element-4151">
                      <select value={selectedTruckType || ''} onChange={e => setSelectedTruckType(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-gray-900 focus:border-gray-900" data-id="element-4152">
                        {truckTypes.map(type => <option key={type} value={type} data-id="element-4153">
                            {type} (
                            {(autoDispatch.truckServiceMap?.[type] || []).length}
                            /{services.length})
                          </option>)}
                      </select>
                    </div>
                    <div className="hidden lg:block p-2 space-y-1" data-id="element-4154">
                      {truckTypes.map(type => {
                    const enabledCount = (autoDispatch.truckServiceMap?.[type] || []).length;
                    const isActive = selectedTruckType === type;
                    const isAll = enabledCount === services.length;
                    const isNone = enabledCount === 0;
                    return <button key={type} onClick={() => setSelectedTruckType(type)} className={`w-full text-left px-3 py-2.5 rounded-lg transition-colors flex items-center justify-between gap-2 ${isActive ? 'bg-white shadow-sm border border-gray-200' : 'hover:bg-white/70 border border-transparent'}`} data-id="element-4155">
                            <div className="min-w-0 flex-1" data-id="element-4156">
                              <div className="text-sm font-semibold text-gray-900 truncate" data-id="element-4157">
                                {type}
                              </div>
                              <div className="text-xs text-gray-500 mt-0.5" data-id="element-4158">
                                {enabledCount} of {services.length} services
                              </div>
                            </div>
                            <span className={`text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded shrink-0 ${isAll ? 'bg-emerald-100 text-emerald-700' : isNone ? 'bg-gray-100 text-gray-500' : 'bg-blue-100 text-blue-700'}`} data-id="element-4159">
                              {isAll ? 'All' : isNone ? 'None' : 'Partial'}
                            </span>
                          </button>;
                  })}
                    </div>
                  </div>

                  {/* Right: Services panel */}
                  {selectedTruckType && <div className="bg-white lg:max-h-[460px] flex flex-col" data-id="element-4160">
                      <div className="p-4 border-b border-gray-100" data-id="element-4161">
                        <div className="flex items-start justify-between gap-3 mb-3" data-id="element-4162">
                          <div data-id="element-4163">
                            <div className="text-sm font-bold text-gray-900" data-id="element-4164">
                              {selectedTruckType}
                            </div>
                            <div className="text-xs text-gray-500 mt-0.5" data-id="element-4165">
                              {selectedEnabled.length} of {services.length}{' '}
                              services enabled
                            </div>
                          </div>
                          <div className="flex items-center gap-1.5 shrink-0" data-id="element-4166">
                            <button onClick={() => handleBulkSet(selectedTruckType, true)} className="text-xs font-medium text-gray-700 hover:text-gray-900 px-2.5 py-1 rounded-md hover:bg-gray-100 transition-colors" data-id="element-4167">
                              Select all
                            </button>
                            <button onClick={() => handleBulkSet(selectedTruckType, false)} className="text-xs font-medium text-gray-700 hover:text-gray-900 px-2.5 py-1 rounded-md hover:bg-gray-100 transition-colors" data-id="element-4168">
                              Clear
                            </button>
                          </div>
                        </div>
                        <div className="relative" data-id="element-4169">
                          <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" data-id="element-4170" />
                          <input type="text" value={serviceSearch} onChange={e => setServiceSearch(e.target.value)} placeholder="Search services..." className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-gray-900 focus:border-gray-900" data-id="element-4171" />
                        </div>
                      </div>

                      <div className="flex-1 overflow-y-auto p-4" data-id="element-4172">
                        {filteredServices.length === 0 ? <div className="text-sm text-gray-500 text-center py-8" data-id="element-4173">
                            No services match "{serviceSearch}"
                          </div> : <div className="flex flex-wrap gap-2" data-id="element-4174">
                            {filteredServices.map(service => {
                      const isSelected = selectedEnabled.includes(service);
                      return <button key={service} onClick={() => handleToggleService(selectedTruckType, service)} className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium transition-colors border ${isSelected ? 'bg-gray-900 text-white border-gray-900 hover:bg-gray-800' : 'bg-white text-gray-700 border-gray-200 hover:border-gray-400'}`} data-id="element-4175">
                                  {isSelected && <Check className="w-3.5 h-3.5" data-id="element-4176" />}
                                  {service}
                                </button>;
                    })}
                          </div>}
                      </div>
                    </div>}
                </div>}
            </div>
          </div>
        </div>
      </div>;
  }
  function BusinessTab() {
    const [isTradeLicenseModalOpen, setIsTradeLicenseModalOpen] = useState(false);
    return <div className="space-y-6" data-id="element-4177">
        <div className="bg-white rounded-2xl border border-gray-200 p-6" data-id="element-4178">
          <div className="mb-6" data-id="element-4179">
            <h2 className="text-lg font-bold text-gray-900" data-id="element-4180">
              Business Details
            </h2>
            <p className="text-sm text-gray-500" data-id="element-4181">
              Information shown to customers and used on invoices
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-4182">
            <div data-id="element-4183">
              <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-4184">
                Business Name
              </label>
              <div className="relative" data-id="element-4185">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-4186">
                  <Building2 className="h-4 w-4 text-gray-400" data-id="element-4187" />
                </div>
                <input type="text" defaultValue={user?.businessName || 'Falcon Logistics LLC'} className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-xl focus:ring-gray-900 focus:border-gray-900 sm:text-sm" data-id="element-4188" />
              </div>
            </div>

            <div data-id="element-4189">
              <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-4190">
                Email Address
              </label>
              <div className="relative" data-id="element-4191">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-4192">
                  <Mail className="h-4 w-4 text-gray-400" data-id="element-4193" />
                </div>
                <input type="email" defaultValue={user?.email || 'contact@falconlogistics.ae'} className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-xl focus:ring-gray-900 focus:border-gray-900 sm:text-sm" data-id="element-4194" />
              </div>
            </div>

            <div data-id="element-4195">
              <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-4196">
                Phone Number
              </label>
              <div className="relative" data-id="element-4197">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-4198">
                  <Phone className="h-4 w-4 text-gray-400" data-id="element-4199" />
                </div>
                <input type="tel" defaultValue="+971 50 123 4567" className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-xl focus:ring-gray-900 focus:border-gray-900 sm:text-sm" data-id="element-4200" />
              </div>
            </div>

            <div data-id="element-4201">
              <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-4202">
                Trade License Number
              </label>
              <div className="relative" data-id="element-4203">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-4204">
                  <FileText className="h-4 w-4 text-gray-400" data-id="element-4205" />
                </div>
                <input type="text" defaultValue="CN-1234567" className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-xl focus:ring-gray-900 focus:border-gray-900 sm:text-sm" data-id="element-4206" />
              </div>
            </div>

            <div className="md:col-span-2" data-id="element-4207">
              <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-4208">
                Trade License Expiry
              </label>
              <div className="flex items-center gap-4" data-id="element-4209">
                <input type="date" defaultValue="2025-12-31" className="block w-full max-w-xs px-3 py-2 border border-gray-300 rounded-xl focus:ring-gray-900 focus:border-gray-900 sm:text-sm" data-id="element-4210" />
                <button onClick={() => setIsTradeLicenseModalOpen(true)} className="text-sm font-medium text-blue-600 hover:text-blue-700" data-id="element-4211">
                  Update License
                </button>
              </div>
            </div>

            <div className="md:col-span-2" data-id="element-4212">
              <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-4213">
                Business Address
              </label>
              <div className="relative" data-id="element-4214">
                <div className="absolute top-3 left-3 pointer-events-none" data-id="element-4215">
                  <MapPin className="h-4 w-4 text-gray-400" data-id="element-4216" />
                </div>
                <textarea rows={3} defaultValue="Office 1502, Boulevard Plaza Tower 2, Downtown Dubai" className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-xl focus:ring-gray-900 focus:border-gray-900 sm:text-sm" data-id="element-4217" />
              </div>
            </div>
          </div>

          <div className="mt-8 border-t border-gray-100 pt-6" data-id="element-4218">
            <h3 className="text-sm font-medium text-gray-900 mb-4" data-id="element-4219">
              Verification Status
            </h3>
            <div className="flex flex-wrap gap-4" data-id="element-4220">
              <div className="flex items-center gap-2 bg-green-50 text-green-700 px-3 py-1.5 rounded-lg text-sm font-medium" data-id="element-4221">
                <CheckCircle2 className="w-4 h-4" data-id="element-4222" />
                Trade License Verified
              </div>
              <div className="flex items-center gap-2 bg-green-50 text-green-700 px-3 py-1.5 rounded-lg text-sm font-medium" data-id="element-4223">
                <CheckCircle2 className="w-4 h-4" data-id="element-4224" />
                VAT Registered
              </div>
              <div className="flex items-center gap-2 bg-green-50 text-green-700 px-3 py-1.5 rounded-lg text-sm font-medium" data-id="element-4225">
                <CheckCircle2 className="w-4 h-4" data-id="element-4226" />
                RTA Approved
              </div>
            </div>
          </div>

          <div className="mt-8 flex justify-end" data-id="element-4227">
            <button onClick={() => toast.success('Business details saved')} className="bg-gray-900 text-white px-5 py-2.5 rounded-xl text-sm font-bold hover:bg-gray-800 transition-colors" data-id="element-4228">
              Save Changes
            </button>
          </div>
        </div>

        <AnimatePresence data-id="element-4229">
          {isTradeLicenseModalOpen && <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-gray-900/50 backdrop-blur-sm" data-id="element-4230">
              <motion.div initial={{
            opacity: 0,
            scale: 0.95
          }} animate={{
            opacity: 1,
            scale: 1
          }} exit={{
            opacity: 0,
            scale: 0.95
          }} className="bg-white rounded-2xl shadow-xl w-full max-w-md overflow-hidden" data-id="element-4231">
                <div className="p-6 border-b border-gray-100 flex justify-between items-center bg-gray-50/50" data-id="element-4232">
                  <h3 className="text-lg font-bold text-gray-900" data-id="element-4233">
                    Update Trade License
                  </h3>
                  <button onClick={() => setIsTradeLicenseModalOpen(false)} className="text-gray-400 hover:text-gray-600" data-id="element-4234">
                    <X className="w-5 h-5" data-id="element-4235" />
                  </button>
                </div>
                <div className="p-6 space-y-6" data-id="element-4236">
                  <div data-id="element-4237">
                    <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-4238">
                      Upload Document
                    </label>
                    <div className="border-2 border-dashed border-gray-200 rounded-xl p-8 text-center hover:bg-gray-50 transition-colors cursor-pointer" data-id="element-4239">
                      <FileText className="w-8 h-8 text-gray-400 mx-auto mb-3" data-id="element-4240" />
                      <p className="text-sm font-medium text-gray-900" data-id="element-4241">
                        Click to upload or drag and drop
                      </p>
                      <p className="text-xs text-gray-500 mt-1" data-id="element-4242">
                        PDF or JPG, max 10MB
                      </p>
                    </div>
                  </div>
                  <div data-id="element-4243">
                    <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-4244">
                      New Expiry Date
                    </label>
                    <input type="date" className="block w-full px-3 py-2 border border-gray-300 rounded-xl focus:ring-gray-900 focus:border-gray-900 sm:text-sm" data-id="element-4245" />
                  </div>
                </div>
                <div className="p-6 border-t border-gray-100 bg-gray-50 flex justify-end gap-3" data-id="element-4246">
                  <button onClick={() => setIsTradeLicenseModalOpen(false)} className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-xl transition-colors" data-id="element-4247">
                    Cancel
                  </button>
                  <button onClick={() => {
                toast.success('Trade license updated');
                setIsTradeLicenseModalOpen(false);
              }} className="px-4 py-2 text-sm font-medium text-white bg-gray-900 hover:bg-gray-800 rounded-xl transition-colors" data-id="element-4248">
                    Save License
                  </button>
                </div>
              </motion.div>
            </div>}
        </AnimatePresence>
      </div>;
  }
  function ServicesTab() {
    const [newService, setNewService] = useState('');
    const [chatOpen, setChatOpen] = useState(false);
    const [chatInput, setChatInput] = useState('');
    const [chatMsgs, setChatMsgs] = useState<ChatMsg[]>([{
      id: '1',
      from: 'ai',
      text: "Hi! I'm your AI Service Advisor. Based on your fleet, I've suggested some new services you could offer. Ask me anything about expanding your business!"
    }]);
    const chatEndRef = useRef<HTMLDivElement>(null);
    useEffect(() => {
      chatEndRef.current?.scrollIntoView({
        behavior: 'smooth'
      });
    }, [chatMsgs]);
    const handleSendChat = () => {
      if (!chatInput.trim()) return;
      const userMsg: ChatMsg = {
        id: Date.now().toString(),
        from: 'user',
        text: chatInput
      };
      setChatMsgs(prev => [...prev, userMsg]);
      setChatInput('');
      setTimeout(() => {
        const aiMsg: ChatMsg = {
          id: (Date.now() + 1).toString(),
          from: 'ai',
          text: "That's a great idea. Given your current truck types, expanding into that area would require adding at least one refrigerated truck to your fleet. Would you like me to analyze the potential ROI?"
        };
        setChatMsgs(prev => [...prev, aiMsg]);
      }, 1000);
    };
    return <div className="space-y-6" data-id="element-4249">
        <div className="bg-white rounded-2xl border border-gray-200 p-6" data-id="element-4250">
          <div className="mb-6 flex items-start justify-between gap-4" data-id="element-4251">
            <div data-id="element-4252">
              <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2" data-id="element-4253">
                <Sparkles className="w-5 h-5 text-blue-600" data-id="element-4254" />
                AI Service Advisor
              </h2>
              <p className="text-sm text-gray-500" data-id="element-4255">
                Discover new services your fleet can offer based on your truck
                types.
              </p>
            </div>
            <button onClick={() => setChatOpen(true)} className="shrink-0 inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-bold text-white bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 shadow-sm hover:shadow-md transition-all" data-id="element-4256">
              <Wand2 className="w-4 h-4" data-id="element-4257" />
              Consult with AI
            </button>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8" data-id="element-4258">
            {/* Left: Current Services */}
            <div data-id="element-4259">
              <h3 className="text-sm font-bold text-gray-900 mb-4" data-id="element-4260">
                Your Services
              </h3>
              <div className="flex gap-2 mb-4" data-id="element-4261">
                <input type="text" value={newService} onChange={e => setNewService(e.target.value)} placeholder="Add a service manually..." className="flex-1 px-3 py-2 border border-gray-300 rounded-xl text-sm focus:ring-gray-900 focus:border-gray-900" onKeyDown={e => {
                if (e.key === 'Enter' && newService.trim()) {
                  addService(newService);
                  setNewService('');
                }
              }} data-id="element-4262" />
                <button onClick={() => {
                if (newService.trim()) {
                  addService(newService);
                  setNewService('');
                }
              }} className="px-4 py-2 bg-gray-900 text-white text-sm font-medium rounded-xl hover:bg-gray-800" data-id="element-4263">
                  Add
                </button>
              </div>
              <div className="space-y-2" data-id="element-4264">
                {services.map(service => <div key={service} className="flex items-center justify-between p-3 bg-gray-50 rounded-xl border border-gray-100" data-id="element-4265">
                    <span className="text-sm font-medium text-gray-900" data-id="element-4266">
                      {service}
                    </span>
                    <button onClick={() => removeService(service)} className="text-gray-400 hover:text-red-500 transition-colors" data-id="element-4267">
                      <X className="w-4 h-4" data-id="element-4268" />
                    </button>
                  </div>)}
                {services.length === 0 && <p className="text-sm text-gray-500 text-center py-4" data-id="element-4269">
                    No services added yet.
                  </p>}
              </div>
            </div>

            {/* Right: AI Suggestions */}
            <div data-id="element-4270">
              <h3 className="text-sm font-bold text-gray-900 mb-4 flex items-center gap-2" data-id="element-4271">
                <Wand2 className="w-4 h-4 text-purple-500" data-id="element-4272" />
                AI Suggestions
              </h3>
              <div className="space-y-3" data-id="element-4273">
                {pendingSuggestions.length > 0 ? pendingSuggestions.map((sugg, idx) => <div key={idx} className="p-4 bg-purple-50/50 border border-purple-100 rounded-xl" data-id="element-4274">
                      <div className="flex items-start justify-between gap-4" data-id="element-4275">
                        <div data-id="element-4276">
                          <h4 className="text-sm font-bold text-gray-900" data-id="element-4277">
                            {sugg.service}
                          </h4>
                          <p className="text-xs text-gray-600 mt-1" data-id="element-4278">
                            {sugg.rationale}
                          </p>
                          <div className="flex flex-wrap gap-1 mt-2" data-id="element-4279">
                            {sugg.basedOn.map((t, i) => <span key={i} className="px-2 py-0.5 bg-white border border-purple-200 text-purple-700 rounded-md text-[10px] font-medium" data-id="element-4280">
                                {t}
                              </span>)}
                          </div>
                        </div>
                        <button onClick={() => addService(sugg.service)} className="shrink-0 p-1.5 bg-white border border-purple-200 text-purple-600 rounded-lg hover:bg-purple-50 transition-colors" title="Add Service" data-id="element-4281">
                          <Plus className="w-4 h-4" data-id="element-4282" />
                        </button>
                      </div>
                    </div>) : <div className="p-6 text-center bg-gray-50 rounded-xl border border-gray-100" data-id="element-4283">
                    <CheckCircle2 className="w-8 h-8 text-green-500 mx-auto mb-2" data-id="element-4284" />
                    <p className="text-sm font-medium text-gray-900" data-id="element-4285">
                      You're offering all suggested services!
                    </p>
                    <p className="text-xs text-gray-500 mt-1" data-id="element-4286">
                      Add more truck types to get new suggestions.
                    </p>
                  </div>}
              </div>
            </div>
          </div>
        </div>

        {/* Chat Drawer */}
        <AnimatePresence data-id="element-4287">
          {chatOpen && <>
              <motion.div initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} exit={{
            opacity: 0
          }} onClick={() => setChatOpen(false)} className="fixed inset-0 bg-gray-900/40 backdrop-blur-sm z-40" data-id="element-4288" />
              <motion.div initial={{
            x: '100%'
          }} animate={{
            x: 0
          }} exit={{
            x: '100%'
          }} transition={{
            type: 'spring',
            damping: 30,
            stiffness: 300
          }} className="fixed right-0 top-0 bottom-0 w-full max-w-md bg-white shadow-2xl z-50 flex flex-col" data-id="element-4289">
                <div className="p-4 border-b border-gray-100 bg-gradient-to-r from-blue-50 to-purple-50 flex items-center justify-between gap-3" data-id="element-4290">
                  <div className="flex items-center gap-3" data-id="element-4291">
                    <div className="w-9 h-9 rounded-full bg-gradient-to-br from-blue-600 to-purple-600 flex items-center justify-center" data-id="element-4292">
                      <Wand2 className="w-4 h-4 text-white" data-id="element-4293" />
                    </div>
                    <div data-id="element-4294">
                      <h3 className="text-sm font-bold text-gray-900" data-id="element-4295">
                        Consult with AI
                      </h3>
                      <p className="text-xs text-gray-500" data-id="element-4296">
                        Ask about market trends, pricing, or fleet expansion
                      </p>
                    </div>
                  </div>
                  <button onClick={() => setChatOpen(false)} className="p-1.5 text-gray-400 hover:text-gray-700 hover:bg-white/60 rounded-lg transition-colors" data-id="element-4297">
                    <X className="w-5 h-5" data-id="element-4298" />
                  </button>
                </div>
                <div className="flex-1 overflow-y-auto p-4 space-y-4" data-id="element-4299">
                  {chatMsgs.map(msg => <div key={msg.id} className={`flex ${msg.from === 'user' ? 'justify-end' : 'justify-start'}`} data-id="element-4300">
                      <div className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm ${msg.from === 'user' ? 'bg-gray-900 text-white rounded-br-sm' : 'bg-gray-100 text-gray-900 rounded-bl-sm'}`} data-id="element-4301">
                        {msg.text}
                      </div>
                    </div>)}
                  <div ref={chatEndRef} data-id="element-4302" />
                </div>
                <div className="p-4 border-t border-gray-100 bg-white" data-id="element-4303">
                  <div className="relative" data-id="element-4304">
                    <input type="text" value={chatInput} onChange={e => setChatInput(e.target.value)} onKeyDown={e => {
                  if (e.key === 'Enter') handleSendChat();
                }} placeholder="Ask a question..." autoFocus className="w-full pl-4 pr-12 py-3 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:ring-gray-900 focus:border-gray-900" data-id="element-4305" />
                    <button onClick={handleSendChat} disabled={!chatInput.trim()} className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 bg-gray-900 text-white rounded-lg disabled:opacity-50 hover:bg-gray-800 transition-colors" data-id="element-4306">
                      <Send className="w-4 h-4" data-id="element-4307" />
                    </button>
                  </div>
                </div>
              </motion.div>
            </>}
        </AnimatePresence>
      </div>;
  }
  function PricingTab() {
    const [isPricingModalOpen, setIsPricingModalOpen] = useState(false);
    const [editingGroup, setEditingGroup] = useState<any>(null);
    const [activeSubTab, setActiveSubTab] = useState<'groups' | 'assignment'>('groups');
    const handleEditGroup = (group: any) => {
      setEditingGroup(group);
      setIsPricingModalOpen(true);
    };
    const assignedCount = services.filter(s => serviceGroupMap[s]).length;
    return <div className="space-y-6" data-id="element-4308">
        {/* Sub-tabs */}
        <div className="bg-white rounded-2xl border border-gray-200 p-1.5 inline-flex gap-1" data-id="element-4309">
          <button onClick={() => setActiveSubTab('groups')} className={`relative flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-colors ${activeSubTab === 'groups' ? 'bg-gray-900 text-white' : 'text-gray-600 hover:bg-gray-50'}`} data-id="element-4310">
            <Tag className="w-4 h-4" data-id="element-4311" />
            Pricing Groups
            <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${activeSubTab === 'groups' ? 'bg-white/15 text-white' : 'bg-gray-100 text-gray-600'}`} data-id="element-4312">
              {pricingGroups.length}
            </span>
          </button>
          <button onClick={() => setActiveSubTab('assignment')} className={`relative flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-colors ${activeSubTab === 'assignment' ? 'bg-gray-900 text-white' : 'text-gray-600 hover:bg-gray-50'}`} data-id="element-4313">
            <Settings2 className="w-4 h-4" data-id="element-4314" />
            Service Assignment
            <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${activeSubTab === 'assignment' ? 'bg-white/15 text-white' : 'bg-gray-100 text-gray-600'}`} data-id="element-4315">
              {assignedCount}/{services.length}
            </span>
          </button>
        </div>

        {activeSubTab === 'groups' && <div className="bg-white rounded-2xl border border-gray-200 p-6" data-id="element-4316">
            <div className="flex items-center justify-between mb-6" data-id="element-4317">
              <div data-id="element-4318">
                <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2" data-id="element-4319">
                  <Tag className="w-5 h-5 text-blue-600" data-id="element-4320" />
                  Pricing Groups
                </h2>
                <p className="text-sm text-gray-500" data-id="element-4321">
                  Create reusable pricing rules — distance-based tiers, flat
                  fees, or city-pair routes.
                </p>
              </div>
              <button onClick={() => {
            setEditingGroup(null);
            setIsPricingModalOpen(true);
          }} className="flex items-center gap-2 px-4 py-2 bg-gray-900 text-white text-sm font-medium rounded-xl hover:bg-gray-800 transition-colors" data-id="element-4322">
                <Plus className="w-4 h-4" data-id="element-4323" />
                Create Group
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-4324">
              {pricingGroups.map(group => {
            const usedByCount = services.filter(s => serviceGroupMap[s] === group.id).length;
            return <div key={group.id} className="border border-gray-200 rounded-xl p-4 hover:border-gray-300 transition-colors" data-id="element-4325">
                    <div className="flex items-start justify-between mb-2" data-id="element-4326">
                      <h3 className="font-bold text-gray-900" data-id="element-4327">{group.name}</h3>
                      <div className="flex items-center gap-1" data-id="element-4328">
                        <button onClick={() => handleEditGroup(group)} className="p-1 text-gray-400 hover:text-gray-900 transition-colors" data-id="element-4329">
                          <Edit2 className="w-4 h-4" data-id="element-4330" />
                        </button>
                        <button onClick={() => deletePricingGroup(group.id)} className="p-1 text-gray-400 hover:text-red-500 transition-colors" data-id="element-4331">
                          <Trash2 className="w-4 h-4" data-id="element-4332" />
                        </button>
                      </div>
                    </div>
                    <div className="flex items-center gap-1.5 mb-3" data-id="element-4333">
                      <span className="px-2 py-0.5 bg-gray-100 text-gray-600 rounded text-xs font-medium uppercase tracking-wider" data-id="element-4334">
                        {group.type}
                      </span>
                      {usedByCount > 0 && <span className="px-2 py-0.5 bg-blue-50 text-blue-700 rounded text-xs font-medium" data-id="element-4335">
                          Used by {usedByCount}
                        </span>}
                    </div>
                    <div className="text-sm text-gray-600" data-id="element-4336">
                      {group.type === 'distance' && <p data-id="element-4337">
                          Base: AED {group.baseFare} • {group.tiers?.length}{' '}
                          tiers
                        </p>}
                      {group.type === 'fixed' && <p data-id="element-4338">Flat Rate: AED {group.flatPrice}</p>}
                      {group.type === 'city' && <p data-id="element-4339">{group.cityPairs?.length} routes defined</p>}
                    </div>
                  </div>;
          })}
              {pricingGroups.length === 0 && <div className="col-span-full p-8 text-center border-2 border-dashed border-gray-200 rounded-xl" data-id="element-4340">
                  <Tag className="w-8 h-8 text-gray-300 mx-auto mb-2" data-id="element-4341" />
                  <p className="text-sm font-medium text-gray-900" data-id="element-4342">
                    No pricing groups yet
                  </p>
                  <p className="text-xs text-gray-500 mt-1" data-id="element-4343">
                    Create a group to set up automated pricing.
                  </p>
                </div>}
            </div>
          </div>}

        {activeSubTab === 'assignment' && <div className="bg-white rounded-2xl border border-gray-200 p-6" data-id="element-4344">
            <div className="mb-6" data-id="element-4345">
              <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2" data-id="element-4346">
                <Settings2 className="w-5 h-5 text-blue-600" data-id="element-4347" />
                Service Assignment
              </h2>
              <p className="text-sm text-gray-500" data-id="element-4348">
                Assign pricing groups to your offered services.{' '}
                {pricingGroups.length === 0 && <button onClick={() => setActiveSubTab('groups')} className="text-blue-600 hover:underline font-medium" data-id="element-4349">
                    Create a pricing group first →
                  </button>}
              </p>
            </div>

            <div className="space-y-2" data-id="element-4350">
              {services.map(service => {
            const assignedGroup = pricingGroups.find(g => g.id === serviceGroupMap[service]);
            return <div key={service} className="flex items-center justify-between gap-3 p-4 bg-gray-50 rounded-xl border border-gray-100 hover:border-gray-200 transition-colors" data-id="element-4351">
                    <div className="min-w-0 flex-1" data-id="element-4352">
                      <p className="font-medium text-gray-900 truncate" data-id="element-4353">
                        {service}
                      </p>
                      <p className="text-xs text-gray-500 mt-0.5" data-id="element-4354">
                        {assignedGroup ? <>
                            Auto-priced via{' '}
                            <span className="font-semibold text-gray-700" data-id="element-4355">
                              {assignedGroup.name}
                            </span>
                          </> : <span className="italic" data-id="element-4356">Manual quote</span>}
                      </p>
                    </div>
                    <select value={serviceGroupMap[service] || ''} onChange={e => assignServiceGroup(service, e.target.value || null)} className="block w-56 sm:w-64 pl-3 pr-10 py-2 text-sm border-gray-300 focus:outline-none focus:ring-gray-900 focus:border-gray-900 rounded-lg shrink-0" data-id="element-4357">
                      <option value="" data-id="element-4358">
                        No automated pricing (Manual quote)
                      </option>
                      {pricingGroups.map(g => <option key={g.id} value={g.id} data-id="element-4359">
                          {g.name} ({g.type})
                        </option>)}
                    </select>
                  </div>;
          })}
              {services.length === 0 && <p className="text-sm text-gray-500 text-center py-8" data-id="element-4360">
                  Add services in the AI Advisor tab first.
                </p>}
            </div>
          </div>}

        <AnimatePresence data-id="element-4361">
          {isPricingModalOpen && <PricingGroupModal initialData={editingGroup} onClose={() => setIsPricingModalOpen(false)} onSave={data => {
          if (editingGroup) {
            updatePricingGroup(editingGroup.id, data);
          } else {
            addPricingGroup({
              ...data,
              id: `pg-${Date.now()}`
            } as any);
          }
          setIsPricingModalOpen(false);
        }} data-id="element-4362" />}
        </AnimatePresence>
      </div>;
  }
  function PricingGroupModal({
    initialData,
    onClose,
    onSave
  }: any) {
    const [step, setStep] = useState(initialData ? 2 : 1);
    const [name, setName] = useState(initialData?.name || '');
    const [type, setType] = useState<any>(initialData?.type || 'distance');
    // Distance state
    const [baseFare, setBaseFare] = useState(initialData?.baseFare || 100);
    const [tiers, setTiers] = useState<any[]>(initialData?.tiers || [{
      id: '1',
      fromKm: 0,
      toKm: 10,
      price: 50
    }]);
    // Fixed state
    const [flatPrice, setFlatPrice] = useState(initialData?.flatPrice || 500);
    // City state
    const [cityPairs, setCityPairs] = useState<any[]>(initialData?.cityPairs || [{
      id: '1',
      origin: 'Dubai',
      destination: 'Abu Dhabi',
      price: 800,
      bothWays: true
    }]);
    const handleSave = () => {
      if (!name.trim()) {
        toast.error('Please enter a name for the pricing group');
        return;
      }
      const data: any = {
        name,
        type
      };
      if (type === 'distance') {
        data.baseFare = baseFare;
        data.tiers = tiers;
      } else if (type === 'fixed') {
        data.flatPrice = flatPrice;
      } else if (type === 'city') {
        data.cityPairs = cityPairs;
      }
      onSave(data);
    };
    return <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-gray-900/50 backdrop-blur-sm" data-id="element-4363">
        <motion.div initial={{
        opacity: 0,
        scale: 0.95
      }} animate={{
        opacity: 1,
        scale: 1
      }} exit={{
        opacity: 0,
        scale: 0.95
      }} className="bg-white rounded-2xl shadow-xl w-full max-w-2xl overflow-hidden flex flex-col max-h-[90vh]" data-id="element-4364">
          <div className="p-6 border-b border-gray-100 flex justify-between items-center bg-gray-50/50 shrink-0" data-id="element-4365">
            <div data-id="element-4366">
              <h3 className="text-lg font-bold text-gray-900" data-id="element-4367">
                {initialData ? 'Edit Pricing Group' : 'Create Pricing Group'}
              </h3>
              <p className="text-sm text-gray-500" data-id="element-4368">
                Step {step} of 2: {step === 1 ? 'Basic Info' : 'Pricing Rules'}
              </p>
            </div>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600" data-id="element-4369">
              <X className="w-5 h-5" data-id="element-4370" />
            </button>
          </div>

          <div className="p-6 overflow-y-auto flex-1" data-id="element-4371">
            {step === 1 && <div className="space-y-6" data-id="element-4372">
                <div data-id="element-4373">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-4374">
                    Group Name
                  </label>
                  <input type="text" value={name} onChange={e => setName(e.target.value)} placeholder="e.g., Standard Local Move" className="block w-full px-3 py-2 border border-gray-300 rounded-xl focus:ring-gray-900 focus:border-gray-900 sm:text-sm" data-id="element-4375" />
                </div>
                <div data-id="element-4376">
                  <label className="block text-sm font-medium text-gray-700 mb-3" data-id="element-4377">
                    Pricing Model
                  </label>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-4378">
                    <button onClick={() => setType('distance')} className={`p-4 rounded-xl border-2 text-left transition-colors ${type === 'distance' ? 'border-gray-900 bg-gray-50' : 'border-gray-200 hover:border-gray-300'}`} data-id="element-4379">
                      <Route className={`w-6 h-6 mb-2 ${type === 'distance' ? 'text-gray-900' : 'text-gray-400'}`} data-id="element-4380" />
                      <h4 className="font-bold text-gray-900 text-sm" data-id="element-4381">
                        Distance Tiers
                      </h4>
                      <p className="text-xs text-gray-500 mt-1" data-id="element-4382">
                        Base fare + price per km slab
                      </p>
                    </button>
                    <button onClick={() => setType('fixed')} className={`p-4 rounded-xl border-2 text-left transition-colors ${type === 'fixed' ? 'border-gray-900 bg-gray-50' : 'border-gray-200 hover:border-gray-300'}`} data-id="element-4383">
                      <Tag className={`w-6 h-6 mb-2 ${type === 'fixed' ? 'text-gray-900' : 'text-gray-400'}`} data-id="element-4384" />
                      <h4 className="font-bold text-gray-900 text-sm" data-id="element-4385">
                        Fixed Price
                      </h4>
                      <p className="text-xs text-gray-500 mt-1" data-id="element-4386">
                        Flat rate regardless of distance
                      </p>
                    </button>
                    <button onClick={() => setType('city')} className={`p-4 rounded-xl border-2 text-left transition-colors ${type === 'city' ? 'border-gray-900 bg-gray-50' : 'border-gray-200 hover:border-gray-300'}`} data-id="element-4387">
                      <MapPinned className={`w-6 h-6 mb-2 ${type === 'city' ? 'text-gray-900' : 'text-gray-400'}`} data-id="element-4388" />
                      <h4 className="font-bold text-gray-900 text-sm" data-id="element-4389">
                        City-to-City
                      </h4>
                      <p className="text-xs text-gray-500 mt-1" data-id="element-4390">
                        Specific rates between emirates
                      </p>
                    </button>
                  </div>
                </div>
              </div>}

            {step === 2 && <div className="space-y-6" data-id="element-4391">
                {type === 'distance' && <>
                    <div data-id="element-4392">
                      <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-4393">
                        Base Fare (AED)
                      </label>
                      <input type="number" value={baseFare} onChange={e => setBaseFare(Number(e.target.value))} className="block w-full max-w-xs px-3 py-2 border border-gray-300 rounded-xl focus:ring-gray-900 focus:border-gray-900 sm:text-sm" data-id="element-4394" />
                      <p className="text-xs text-gray-500 mt-1" data-id="element-4395">
                        Applied before any distance calculations.
                      </p>
                    </div>
                    <div data-id="element-4396">
                      <div className="flex items-center justify-between mb-3" data-id="element-4397">
                        <label className="block text-sm font-medium text-gray-700" data-id="element-4398">
                          Distance Tiers
                        </label>
                        <button onClick={() => setTiers([...tiers, {
                    id: Date.now().toString(),
                    fromKm: tiers.length > 0 ? tiers[tiers.length - 1].toKm || 0 : 0,
                    toKm: null,
                    price: 0
                  }])} className="text-sm font-medium text-blue-600 hover:text-blue-700 flex items-center gap-1" data-id="element-4399">
                          <Plus className="w-4 h-4" data-id="element-4400" /> Add Tier
                        </button>
                      </div>
                      <div className="space-y-3" data-id="element-4401">
                        {tiers.map((tier, idx) => <div key={tier.id} className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl border border-gray-100" data-id="element-4402">
                            <div className="flex-1 grid grid-cols-3 gap-3" data-id="element-4403">
                              <div data-id="element-4404">
                                <span className="text-xs text-gray-500 block mb-1" data-id="element-4405">
                                  From (Km)
                                </span>
                                <input type="number" value={tier.fromKm} onChange={e => {
                          const newTiers = [...tiers];
                          newTiers[idx].fromKm = Number(e.target.value);
                          setTiers(newTiers);
                        }} className="block w-full px-2 py-1.5 border border-gray-300 rounded-lg text-sm" data-id="element-4406" />
                              </div>
                              <div data-id="element-4407">
                                <span className="text-xs text-gray-500 block mb-1" data-id="element-4408">
                                  To (Km)
                                </span>
                                <input type="number" value={tier.toKm || ''} placeholder="Max" onChange={e => {
                          const newTiers = [...tiers];
                          newTiers[idx].toKm = e.target.value ? Number(e.target.value) : null;
                          setTiers(newTiers);
                        }} className="block w-full px-2 py-1.5 border border-gray-300 rounded-lg text-sm" data-id="element-4409" />
                              </div>
                              <div data-id="element-4410">
                                <span className="text-xs text-gray-500 block mb-1" data-id="element-4411">
                                  Price (AED)
                                </span>
                                <input type="number" value={tier.price} onChange={e => {
                          const newTiers = [...tiers];
                          newTiers[idx].price = Number(e.target.value);
                          setTiers(newTiers);
                        }} className="block w-full px-2 py-1.5 border border-gray-300 rounded-lg text-sm" data-id="element-4412" />
                              </div>
                            </div>
                            <button onClick={() => setTiers(tiers.filter(t => t.id !== tier.id))} className="p-2 text-gray-400 hover:text-red-500 mt-5" data-id="element-4413">
                              <Trash2 className="w-4 h-4" data-id="element-4414" />
                            </button>
                          </div>)}
                      </div>
                    </div>
                  </>}

                {type === 'fixed' && <div data-id="element-4415">
                    <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-4416">
                      Flat Price (AED)
                    </label>
                    <input type="number" value={flatPrice} onChange={e => setFlatPrice(Number(e.target.value))} className="block w-full max-w-xs px-3 py-2 border border-gray-300 rounded-xl focus:ring-gray-900 focus:border-gray-900 sm:text-sm" data-id="element-4417" />
                    <p className="text-xs text-gray-500 mt-1" data-id="element-4418">
                      This price will be quoted regardless of distance or time.
                    </p>
                  </div>}

                {type === 'city' && <div data-id="element-4419">
                    <div className="flex items-center justify-between mb-3" data-id="element-4420">
                      <label className="block text-sm font-medium text-gray-700" data-id="element-4421">
                        Defined Routes
                      </label>
                      <button onClick={() => setCityPairs([...cityPairs, {
                  id: Date.now().toString(),
                  origin: UAE_CITIES[0],
                  destination: UAE_CITIES[1],
                  price: 0,
                  bothWays: true
                }])} className="text-sm font-medium text-blue-600 hover:text-blue-700 flex items-center gap-1" data-id="element-4422">
                        <Plus className="w-4 h-4" data-id="element-4423" /> Add Route
                      </button>
                    </div>
                    <div className="space-y-3" data-id="element-4424">
                      {cityPairs.map((pair, idx) => <div key={pair.id} className="p-4 bg-gray-50 rounded-xl border border-gray-100" data-id="element-4425">
                          <div className="flex items-start gap-3" data-id="element-4426">
                            <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 gap-3" data-id="element-4427">
                              <div data-id="element-4428">
                                <span className="text-xs text-gray-500 block mb-1" data-id="element-4429">
                                  Origin
                                </span>
                                <select value={pair.origin} onChange={e => {
                          const newPairs = [...cityPairs];
                          newPairs[idx].origin = e.target.value;
                          setCityPairs(newPairs);
                        }} className="block w-full px-2 py-1.5 border border-gray-300 rounded-lg text-sm" data-id="element-4430">
                                  {UAE_CITIES.map(city => <option key={city} value={city} data-id="element-4431">
                                      {city}
                                    </option>)}
                                </select>
                              </div>
                              <div data-id="element-4432">
                                <span className="text-xs text-gray-500 block mb-1" data-id="element-4433">
                                  Destination
                                </span>
                                <select value={pair.destination} onChange={e => {
                          const newPairs = [...cityPairs];
                          newPairs[idx].destination = e.target.value;
                          setCityPairs(newPairs);
                        }} className="block w-full px-2 py-1.5 border border-gray-300 rounded-lg text-sm" data-id="element-4434">
                                  {UAE_CITIES.map(city => <option key={city} value={city} data-id="element-4435">
                                      {city}
                                    </option>)}
                                </select>
                              </div>
                              <div data-id="element-4436">
                                <span className="text-xs text-gray-500 block mb-1" data-id="element-4437">
                                  Price (AED)
                                </span>
                                <input type="number" value={pair.price} onChange={e => {
                          const newPairs = [...cityPairs];
                          newPairs[idx].price = Number(e.target.value);
                          setCityPairs(newPairs);
                        }} className="block w-full px-2 py-1.5 border border-gray-300 rounded-lg text-sm" data-id="element-4438" />
                              </div>
                              <div className="flex items-center mt-5" data-id="element-4439">
                                <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer" data-id="element-4440">
                                  <input type="checkbox" checked={pair.bothWays} onChange={e => {
                            const newPairs = [...cityPairs];
                            newPairs[idx].bothWays = e.target.checked;
                            setCityPairs(newPairs);
                          }} className="rounded border-gray-300 text-gray-900 focus:ring-gray-900" data-id="element-4441" />
                                  Applies both ways
                                </label>
                              </div>
                            </div>
                            <button onClick={() => setCityPairs(cityPairs.filter(p => p.id !== pair.id))} className="p-2 text-gray-400 hover:text-red-500 mt-5 shrink-0" data-id="element-4442">
                              <Trash2 className="w-4 h-4" data-id="element-4443" />
                            </button>
                          </div>
                        </div>)}
                    </div>
                  </div>}
              </div>}
          </div>

          <div className="p-6 border-t border-gray-100 bg-gray-50 flex justify-between shrink-0" data-id="element-4444">
            {step === 2 ? <button onClick={() => setStep(1)} className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-200 bg-gray-100 rounded-xl transition-colors" data-id="element-4445">
                Back
              </button> : <div data-id="element-4446"></div>}
            <div className="flex gap-3" data-id="element-4447">
              <button onClick={onClose} className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-xl transition-colors" data-id="element-4448">
                Cancel
              </button>
              {step === 1 ? <button onClick={() => {
              if (!name.trim()) {
                toast.error('Please enter a name');
                return;
              }
              setStep(2);
            }} className="px-4 py-2 text-sm font-medium text-white bg-gray-900 hover:bg-gray-800 rounded-xl transition-colors flex items-center gap-2" data-id="element-4449">
                  Next <ArrowRight className="w-4 h-4" data-id="element-4450" />
                </button> : <button onClick={handleSave} className="px-4 py-2 text-sm font-medium text-white bg-gray-900 hover:bg-gray-800 rounded-xl transition-colors" data-id="element-4451">
                  Save Group
                </button>}
            </div>
          </div>
        </motion.div>
      </div>;
  }
  function UsersTab() {
    const [activeSubTab, setActiveSubTab] = useState<'users' | 'roles' | 'audit'>('users');
    // Mock Data
    const [users, setUsers] = useState([{
      id: '1',
      name: 'Admin User',
      email: 'admin@falcon.ae',
      phone: '+971 50 111 2222',
      role: 'Owner',
      status: 'Active',
      hasPassword: true
    }, {
      id: '2',
      name: 'Sarah Dispatch',
      email: 'sarah@falcon.ae',
      phone: '+971 50 333 4444',
      role: 'Dispatcher',
      status: 'Active',
      hasPassword: true
    }]);
    const [roles, setRoles] = useState([{
      id: 'r1',
      name: 'Owner',
      description: 'Full system access',
      isSystem: true
    }, {
      id: 'r2',
      name: 'Dispatcher',
      description: 'Manage jobs and drivers',
      isSystem: false
    }, {
      id: 'r3',
      name: 'Finance',
      description: 'View payments and invoices',
      isSystem: false
    }]);
    const [auditLogs] = useState([{
      id: 'a1',
      action: 'Invited user Sarah Dispatch',
      user: 'Admin User',
      timestamp: '2 hours ago'
    }, {
      id: 'a2',
      action: 'Changed role for John Doe to Driver Manager',
      user: 'Admin User',
      timestamp: '1 day ago'
    }, {
      id: 'a3',
      action: 'Created role Finance',
      user: 'Admin User',
      timestamp: '2 days ago'
    }]);
    const [isInviteModalOpen, setIsInviteModalOpen] = useState(false);
    const [isUserDetailModalOpen, setIsUserDetailModalOpen] = useState(false);
    const [selectedUser, setSelectedUser] = useState<any>(null);
    const [isCreateRoleModalOpen, setIsCreateRoleModalOpen] = useState(false);
    return <div className="space-y-6" data-id="element-4452">
        <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden" data-id="element-4453">
          <div className="p-6 border-b border-gray-100" data-id="element-4454">
            <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2" data-id="element-4455">
              <Users className="w-5 h-5 text-blue-600" data-id="element-4456" />
              Users & Roles
            </h2>
            <p className="text-sm text-gray-500 mt-1" data-id="element-4457">
              Manage team access and permissions
            </p>

            {/* Internal Tabs */}
            <div className="flex gap-2 mt-6" data-id="element-4458">
              <button onClick={() => setActiveSubTab('users')} className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${activeSubTab === 'users' ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`} data-id="element-4459">
                Users
              </button>
              <button onClick={() => setActiveSubTab('roles')} className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${activeSubTab === 'roles' ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`} data-id="element-4460">
                Roles
              </button>
              <button onClick={() => setActiveSubTab('audit')} className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${activeSubTab === 'audit' ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`} data-id="element-4461">
                Audit Log
              </button>
            </div>
          </div>

          <div className="p-0" data-id="element-4462">
            {activeSubTab === 'users' && <div data-id="element-4463">
                <div className="p-4 flex justify-end border-b border-gray-100" data-id="element-4464">
                  <button onClick={() => setIsInviteModalOpen(true)} className="flex items-center gap-2 px-4 py-2 bg-gray-900 text-white text-sm font-medium rounded-xl hover:bg-gray-800 transition-colors" data-id="element-4465">
                    <UserPlus className="w-4 h-4" data-id="element-4466" />
                    Invite User
                  </button>
                </div>
                <div className="overflow-x-auto" data-id="element-4467">
                  <table className="w-full text-sm text-left" data-id="element-4468">
                    <thead className="text-xs text-gray-500 bg-gray-50 uppercase" data-id="element-4469">
                      <tr data-id="element-4470">
                        <th className="px-6 py-3" data-id="element-4471">User</th>
                        <th className="px-6 py-3" data-id="element-4472">Role</th>
                        <th className="px-6 py-3" data-id="element-4473">Status</th>
                        <th className="px-6 py-3 text-right" data-id="element-4474">Actions</th>
                      </tr>
                    </thead>
                    <tbody data-id="element-4475">
                      {users.map(u => <tr key={u.id} className="border-b border-gray-100 hover:bg-gray-50 transition-colors cursor-pointer" onClick={() => {
                    setSelectedUser(u);
                    setIsUserDetailModalOpen(true);
                  }} data-id="element-4476">
                          <td className="px-6 py-4" data-id="element-4477">
                            <div className="font-medium text-gray-900" data-id="element-4478">
                              {u.name}
                            </div>
                            <div className="text-gray-500" data-id="element-4479">{u.email}</div>
                          </td>
                          <td className="px-6 py-4" data-id="element-4480">
                            <span className="px-2.5 py-1 bg-blue-50 text-blue-700 rounded-md text-xs font-medium" data-id="element-4481">
                              {u.role}
                            </span>
                          </td>
                          <td className="px-6 py-4" data-id="element-4482">
                            <span className="flex items-center gap-1.5 text-green-600 font-medium" data-id="element-4483">
                              <div className="w-1.5 h-1.5 rounded-full bg-green-500" data-id="element-4484"></div>
                              {u.status}
                            </span>
                          </td>
                          <td className="px-6 py-4 text-right" data-id="element-4485">
                            <button className="p-1.5 text-gray-400 hover:text-gray-900 rounded-lg hover:bg-gray-200" onClick={e => {
                        e.stopPropagation();
                        setSelectedUser(u);
                        setIsUserDetailModalOpen(true);
                      }} data-id="element-4486">
                              <MoreVertical className="w-4 h-4" data-id="element-4487" />
                            </button>
                          </td>
                        </tr>)}
                    </tbody>
                  </table>
                </div>
              </div>}

            {activeSubTab === 'roles' && <div data-id="element-4488">
                <div className="p-4 flex justify-end border-b border-gray-100" data-id="element-4489">
                  <button onClick={() => setIsCreateRoleModalOpen(true)} className="flex items-center gap-2 px-4 py-2 bg-gray-900 text-white text-sm font-medium rounded-xl hover:bg-gray-800 transition-colors" data-id="element-4490">
                    <Plus className="w-4 h-4" data-id="element-4491" />
                    Create Role
                  </button>
                </div>
                <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-4492">
                  {roles.map(r => <div key={r.id} className="border border-gray-200 rounded-xl p-4" data-id="element-4493">
                      <div className="flex justify-between items-start mb-2" data-id="element-4494">
                        <h3 className="font-bold text-gray-900 flex items-center gap-2" data-id="element-4495">
                          {r.name}
                          {r.isSystem && <span className="px-2 py-0.5 bg-gray-100 text-gray-600 rounded text-[10px] uppercase font-bold tracking-wider" data-id="element-4496">
                              System
                            </span>}
                        </h3>
                        {!r.isSystem && <button className="text-gray-400 hover:text-red-500" data-id="element-4497">
                            <Trash2 className="w-4 h-4" data-id="element-4498" />
                          </button>}
                      </div>
                      <p className="text-sm text-gray-500" data-id="element-4499">{r.description}</p>
                    </div>)}
                </div>
              </div>}

            {activeSubTab === 'audit' && <div className="p-0" data-id="element-4500">
                <div className="divide-y divide-gray-100" data-id="element-4501">
                  {auditLogs.map(log => <div key={log.id} className="p-4 flex items-start gap-4 hover:bg-gray-50" data-id="element-4502">
                      <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center shrink-0 mt-1" data-id="element-4503">
                        <FileText className="w-4 h-4 text-gray-500" data-id="element-4504" />
                      </div>
                      <div data-id="element-4505">
                        <p className="text-sm font-medium text-gray-900" data-id="element-4506">
                          {log.action}
                        </p>
                        <p className="text-xs text-gray-500 mt-0.5" data-id="element-4507">
                          By {log.user} • {log.timestamp}
                        </p>
                      </div>
                    </div>)}
                </div>
              </div>}
          </div>
        </div>

        {/* Modals */}
        <AnimatePresence data-id="element-4508">
          {isInviteModalOpen && <InviteUserModal onClose={() => setIsInviteModalOpen(false)} roles={roles} data-id="element-4509" />}
          {isUserDetailModalOpen && selectedUser && <UserDetailModal user={selectedUser} onClose={() => setIsUserDetailModalOpen(false)} roles={roles} data-id="element-4510" />}
          {isCreateRoleModalOpen && <CreateRoleModal onClose={() => setIsCreateRoleModalOpen(false)} data-id="element-4511" />}
        </AnimatePresence>
      </div>;
  }
  function InviteUserModal({
    onClose,
    roles
  }: any) {
    const [showPassword, setShowPassword] = useState(false);
    return <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-gray-900/50 backdrop-blur-sm" data-id="element-4512">
        <motion.div initial={{
        opacity: 0,
        scale: 0.95
      }} animate={{
        opacity: 1,
        scale: 1
      }} exit={{
        opacity: 0,
        scale: 0.95
      }} className="bg-white rounded-2xl shadow-xl w-full max-w-md overflow-hidden" data-id="element-4513">
          <div className="p-6 border-b border-gray-100 flex justify-between items-center" data-id="element-4514">
            <h3 className="text-lg font-bold text-gray-900" data-id="element-4515">Invite User</h3>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600" data-id="element-4516">
              <X className="w-5 h-5" data-id="element-4517" />
            </button>
          </div>
          <div className="p-6 space-y-4" data-id="element-4518">
            <div data-id="element-4519">
              <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-4520">
                Full Name
              </label>
              <input type="text" className="block w-full px-3 py-2 border border-gray-300 rounded-xl focus:ring-gray-900 focus:border-gray-900 sm:text-sm" data-id="element-4521" />
            </div>
            <div data-id="element-4522">
              <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-4523">
                Email Address
              </label>
              <input type="email" className="block w-full px-3 py-2 border border-gray-300 rounded-xl focus:ring-gray-900 focus:border-gray-900 sm:text-sm" data-id="element-4524" />
            </div>
            <div data-id="element-4525">
              <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-4526">
                Phone Number
              </label>
              <input type="tel" className="block w-full px-3 py-2 border border-gray-300 rounded-xl focus:ring-gray-900 focus:border-gray-900 sm:text-sm" data-id="element-4527" />
            </div>
            <div data-id="element-4528">
              <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-4529">
                Role
              </label>
              <select className="block w-full px-3 py-2 border border-gray-300 rounded-xl focus:ring-gray-900 focus:border-gray-900 sm:text-sm" data-id="element-4530">
                {roles.map((r: any) => <option key={r.id} value={r.id} data-id="element-4531">
                    {r.name}
                  </option>)}
              </select>
            </div>
            <div data-id="element-4532">
              <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-4533">
                Initial Password
              </label>
              <div className="relative" data-id="element-4534">
                <input type={showPassword ? 'text' : 'password'} className="block w-full px-3 py-2 pr-10 border border-gray-300 rounded-xl focus:ring-gray-900 focus:border-gray-900 sm:text-sm" data-id="element-4535" />
                <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600" data-id="element-4536">
                  {showPassword ? <EyeOff className="w-4 h-4" data-id="element-4537" /> : <Eye className="w-4 h-4" data-id="element-4538" />}
                </button>
              </div>
            </div>
          </div>
          <div className="p-6 border-t border-gray-100 bg-gray-50 flex justify-end gap-3" data-id="element-4539">
            <button onClick={onClose} className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-xl" data-id="element-4540">
              Cancel
            </button>
            <button onClick={() => {
            toast.success('User invited');
            onClose();
          }} className="px-4 py-2 text-sm font-medium text-white bg-gray-900 hover:bg-gray-800 rounded-xl" data-id="element-4541">
              Send Invite
            </button>
          </div>
        </motion.div>
      </div>;
  }
  function UserDetailModal({
    user,
    onClose,
    roles
  }: any) {
    const [showPassword, setShowPassword] = useState(false);
    return <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-gray-900/50 backdrop-blur-sm" data-id="element-4542">
        <motion.div initial={{
        opacity: 0,
        scale: 0.95
      }} animate={{
        opacity: 1,
        scale: 1
      }} exit={{
        opacity: 0,
        scale: 0.95
      }} className="bg-white rounded-2xl shadow-xl w-full max-w-md overflow-hidden" data-id="element-4543">
          <div className="p-6 border-b border-gray-100 flex justify-between items-center" data-id="element-4544">
            <h3 className="text-lg font-bold text-gray-900" data-id="element-4545">Edit User</h3>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600" data-id="element-4546">
              <X className="w-5 h-5" data-id="element-4547" />
            </button>
          </div>
          <div className="p-6 space-y-4" data-id="element-4548">
            <div data-id="element-4549">
              <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-4550">
                Full Name
              </label>
              <input type="text" defaultValue={user.name} className="block w-full px-3 py-2 border border-gray-300 rounded-xl focus:ring-gray-900 focus:border-gray-900 sm:text-sm" data-id="element-4551" />
            </div>
            <div data-id="element-4552">
              <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-4553">
                Email Address
              </label>
              <input type="email" defaultValue={user.email} className="block w-full px-3 py-2 border border-gray-300 rounded-xl focus:ring-gray-900 focus:border-gray-900 sm:text-sm" data-id="element-4554" />
            </div>
            <div data-id="element-4555">
              <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-4556">
                Phone Number
              </label>
              <input type="tel" defaultValue={user.phone} className="block w-full px-3 py-2 border border-gray-300 rounded-xl focus:ring-gray-900 focus:border-gray-900 sm:text-sm" data-id="element-4557" />
            </div>
            <div data-id="element-4558">
              <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-4559">
                Role
              </label>
              <select defaultValue={user.role} disabled={user.role === 'Owner'} className="block w-full px-3 py-2 border border-gray-300 rounded-xl focus:ring-gray-900 focus:border-gray-900 sm:text-sm disabled:bg-gray-100 disabled:text-gray-500" data-id="element-4560">
                {roles.map((r: any) => <option key={r.id} value={r.name} data-id="element-4561">
                    {r.name}
                  </option>)}
              </select>
            </div>
            <hr className="border-gray-100 my-4" data-id="element-4562" />
            <div data-id="element-4563">
              <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-4564">
                Reset Password
              </label>
              <div className="relative" data-id="element-4565">
                <input type={showPassword ? 'text' : 'password'} placeholder="Enter new password" className="block w-full px-3 py-2 pr-10 border border-gray-300 rounded-xl focus:ring-gray-900 focus:border-gray-900 sm:text-sm" data-id="element-4566" />
                <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600" data-id="element-4567">
                  {showPassword ? <EyeOff className="w-4 h-4" data-id="element-4568" /> : <Eye className="w-4 h-4" data-id="element-4569" />}
                </button>
              </div>
            </div>
          </div>
          <div className="p-6 border-t border-gray-100 bg-gray-50 flex justify-between items-center" data-id="element-4570">
            {user.role !== 'Owner' ? <button className="text-sm font-medium text-red-600 hover:text-red-700" data-id="element-4571">
                Remove User
              </button> : <div data-id="element-4572"></div>}
            <div className="flex gap-3" data-id="element-4573">
              <button onClick={onClose} className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-xl" data-id="element-4574">
                Cancel
              </button>
              <button onClick={() => {
              toast.success('User updated');
              onClose();
            }} className="px-4 py-2 text-sm font-medium text-white bg-gray-900 hover:bg-gray-800 rounded-xl" data-id="element-4575">
                Save Changes
              </button>
            </div>
          </div>
        </motion.div>
      </div>;
  }
  function CreateRoleModal({
    onClose
  }: any) {
    return <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-gray-900/50 backdrop-blur-sm" data-id="element-4576">
        <motion.div initial={{
        opacity: 0,
        scale: 0.95
      }} animate={{
        opacity: 1,
        scale: 1
      }} exit={{
        opacity: 0,
        scale: 0.95
      }} className="bg-white rounded-2xl shadow-xl w-full max-w-md overflow-hidden" data-id="element-4577">
          <div className="p-6 border-b border-gray-100 flex justify-between items-center" data-id="element-4578">
            <h3 className="text-lg font-bold text-gray-900" data-id="element-4579">Create Role</h3>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600" data-id="element-4580">
              <X className="w-5 h-5" data-id="element-4581" />
            </button>
          </div>
          <div className="p-6 space-y-4" data-id="element-4582">
            <div data-id="element-4583">
              <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-4584">
                Role Name
              </label>
              <input type="text" placeholder="e.g., Junior Dispatcher" className="block w-full px-3 py-2 border border-gray-300 rounded-xl focus:ring-gray-900 focus:border-gray-900 sm:text-sm" data-id="element-4585" />
            </div>
            <div data-id="element-4586">
              <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-4587">
                Description
              </label>
              <input type="text" placeholder="Brief description of responsibilities" className="block w-full px-3 py-2 border border-gray-300 rounded-xl focus:ring-gray-900 focus:border-gray-900 sm:text-sm" data-id="element-4588" />
            </div>
            <div className="pt-2" data-id="element-4589">
              <label className="block text-sm font-bold text-gray-900 mb-3" data-id="element-4590">
                Permissions
              </label>
              <div className="space-y-3" data-id="element-4591">
                <label className="flex items-center gap-2 text-sm text-gray-700" data-id="element-4592">
                  <input type="checkbox" className="rounded border-gray-300 text-gray-900 focus:ring-gray-900" data-id="element-4593" />{' '}
                  Manage Jobs & Dispatch
                </label>
                <label className="flex items-center gap-2 text-sm text-gray-700" data-id="element-4594">
                  <input type="checkbox" className="rounded border-gray-300 text-gray-900 focus:ring-gray-900" data-id="element-4595" />{' '}
                  View Financials
                </label>
                <label className="flex items-center gap-2 text-sm text-gray-700" data-id="element-4596">
                  <input type="checkbox" className="rounded border-gray-300 text-gray-900 focus:ring-gray-900" data-id="element-4597" />{' '}
                  Manage Fleet & Drivers
                </label>
                <label className="flex items-center gap-2 text-sm text-gray-700" data-id="element-4598">
                  <input type="checkbox" className="rounded border-gray-300 text-gray-900 focus:ring-gray-900" data-id="element-4599" />{' '}
                  Edit Settings
                </label>
              </div>
            </div>
          </div>
          <div className="p-6 border-t border-gray-100 bg-gray-50 flex justify-end gap-3" data-id="element-4600">
            <button onClick={onClose} className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-xl" data-id="element-4601">
              Cancel
            </button>
            <button onClick={() => {
            toast.success('Role created');
            onClose();
          }} className="px-4 py-2 text-sm font-medium text-white bg-gray-900 hover:bg-gray-800 rounded-xl" data-id="element-4602">
              Create Role
            </button>
          </div>
        </motion.div>
      </div>;
  }
  function SecurityTab() {
    return <div className="space-y-6" data-id="element-4603">
        <div className="bg-white rounded-2xl border border-gray-200 p-6" data-id="element-4604">
          <div className="mb-6" data-id="element-4605">
            <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2" data-id="element-4606">
              <Lock className="w-5 h-5 text-blue-600" data-id="element-4607" />
              Security Settings
            </h2>
            <p className="text-sm text-gray-500" data-id="element-4608">
              Manage your password and account security.
            </p>
          </div>

          <div className="max-w-md space-y-4" data-id="element-4609">
            <div data-id="element-4610">
              <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-4611">
                Current Password
              </label>
              <input type="password" placeholder="••••••••" className="block w-full px-3 py-2 border border-gray-300 rounded-xl focus:ring-gray-900 focus:border-gray-900 sm:text-sm" data-id="element-4612" />
            </div>
            <div data-id="element-4613">
              <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-4614">
                New Password
              </label>
              <input type="password" placeholder="••••••••" className="block w-full px-3 py-2 border border-gray-300 rounded-xl focus:ring-gray-900 focus:border-gray-900 sm:text-sm" data-id="element-4615" />
            </div>
            <div data-id="element-4616">
              <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-4617">
                Confirm New Password
              </label>
              <input type="password" placeholder="••••••••" className="block w-full px-3 py-2 border border-gray-300 rounded-xl focus:ring-gray-900 focus:border-gray-900 sm:text-sm" data-id="element-4618" />
            </div>

            <div className="pt-4 border-t border-gray-100 mt-6" data-id="element-4619">
              <div className="flex items-center justify-between" data-id="element-4620">
                <div data-id="element-4621">
                  <h4 className="text-sm font-bold text-gray-900" data-id="element-4622">
                    Two-Factor Authentication
                  </h4>
                  <p className="text-xs text-gray-500 mt-1" data-id="element-4623">
                    Add an extra layer of security to your account.
                  </p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer" data-id="element-4624">
                  <input type="checkbox" className="sr-only peer" data-id="element-4625" />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-gray-900" data-id="element-4626"></div>
                </label>
              </div>
            </div>

            <div className="pt-6 flex justify-end" data-id="element-4627">
              <button onClick={() => toast.success('Security settings updated')} className="bg-gray-900 text-white px-5 py-2.5 rounded-xl text-sm font-bold hover:bg-gray-800 transition-colors" data-id="element-4628">
                Update Security
              </button>
            </div>
          </div>
        </div>
      </div>;
  }
}